"use client";

import { PortableText } from "@portabletext/react";
import { cn } from "@/lib/utils";
import { GroqImage } from "@/helpers/Image";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { PageTextOnlyCryptoSchema } from "@/sanity/pages/PageTextOnlyCryptoTemplate";
import CheckList from "./CheckList";
import { DarkHalftone } from "./textures";

/** Card CTA sizing: 238×46 from 640px up, full width below. */
export const CARD_BUTTON_CLASS =
  "max-sm:!w-full sm:!w-auto sm:self-start justify-center max-sm:[&_.button]:!w-full sm:[&_.button]:!w-[29.75rem] [&_.button]:!min-h-[5.75rem] [&_.button]:!rounded-[0.25rem] [&_.button]:!px-[3.5rem] [&_.button]:!text-[1.75rem] [&_.button]:!tracking-normal";

export default function OfferSection({ data, className }: { data: PageTextOnlyCryptoSchema; className?: string }) {
  return (
    <section
      id="chapter-3"
      data-chapter
      className={cn(
        "relative flex justify-center w-full bg-[#001B14] px-[2rem] lg:px-0 py-[8rem] lg:py-[12.5rem]",
        className,
      )}>
      <DarkHalftone />

      <div className="relative flex w-[min(94.5rem,100%)] flex-col overflow-clip rounded-[1rem] bg-white">
        {(data?.offerImage || data?.offerPill) && (
          <div className="relative h-[41.625rem] w-full overflow-clip">
            {data?.offerImage && (
              <GroqImage
                src={data.offerImage}
                alt=""
                quality={100}
                className="object-cover"
                style={{
                  width: "calc(100% + 2rem)",
                  height: "calc(100% + 1.442rem)",
                }}
              />
            )}
            {data?.offerPill && (
              <div className="absolute bottom-0 left-0 flex items-center rounded-tr-[0.75rem] bg-[#1DA17F] px-[1.875rem] py-[1.125rem]">
                <p className="mb-0 font-geist text-[1.75rem] leading-normal font-medium whitespace-nowrap text-white">
                  {data.offerPill}
                </p>
              </div>
            )}
          </div>
        )}

        <div className="flex w-full flex-col gap-[2.25rem] max-lg:px-[2rem] max-lg:py-[3rem] lg:p-[5.25rem]">
          {data?.offerTitle && (
            <div className="richtext richtext-start max-lg:w-[min(42.125rem,100%)] max-lg:[text-wrap:wrap] lg:w-[62.25rem] prose-p:mb-0 prose-p:font-geist prose-p:leading-[1.12] prose-p:font-medium prose-p:text-[#001B14] max-lg:prose-p:text-[3.5rem] lg:prose-p:text-[4rem]">
              <PortableText
                value={data.offerTitle}
                components={richTextComponent}
              />
            </div>
          )}

          <div className="h-px w-full bg-[#001B14]/10" />

          <div className="flex w-full flex-col gap-[1.25rem]">
            {data?.offerSubhead && (
              <p className="mb-0 font-geist leading-[1.5] font-medium text-[#001B14] max-lg:text-[2.5rem] lg:text-[2.75rem]">
                {data.offerSubhead}
              </p>
            )}
            {data?.offerText && (
              <div className="richtext richtext-start w-full prose-p:mb-[1.25rem] prose-p:font-geist prose-p:text-[2.25rem] prose-p:leading-[1.6] prose-p:font-normal prose-p:text-[#001B14]/[0.74]">
                <PortableText
                  value={data.offerText}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          {data?.offerSubhead2 && (
            <p className="mb-0 font-geist leading-[1.5] font-medium text-[#001B14] max-lg:text-[2.5rem] lg:text-[2.75rem]">
              {data.offerSubhead2}
            </p>
          )}

          <CheckList
            items={data?.offerItems}
            checkAlign="top"
            paddingClassName="max-lg:p-[2rem] lg:p-[3rem]"
            panelClassName="bg-[#F4F7F5]"
            textClassName="max-lg:w-[min(40rem,100%)] max-lg:[text-wrap:wrap] lg:w-[64.25rem]"
          />

          {data?.offerCTA && (
            <CTAButton
              data={data.offerCTA}
              overrideVariant="default"
              className={CARD_BUTTON_CLASS}
            />
          )}
        </div>
      </div>
    </section>
  );
}
